import type {WorldPoint} from "@/dungeons/model/DungeonGeometry.ts";
import {PIXELS_PER_METER} from "@/dungeons/geometry/Coordinates.ts";
import {hasLineOfSight, type Segment} from "@/tabletop/visibility/VisibilityGeometry.ts";
import type {LitArea} from "@/tabletop/visibility/VisibilityModel.ts";
import type {DirectionalLight} from "./DirectionalLight.ts";
import {lightAnimationFactor} from "./LightAnimation.ts";

export type LightingBounds = {minX: number; minY: number; maxX: number; maxY: number};
export type LightingEnvironment = {bounds: LightingBounds; blockers: Segment[]; directionalLight: DirectionalLight};
export type LightingScene = LightingEnvironment & {lights: LitArea[]};

/** Map coordinates use positive Y down; direction denotes where the light comes FROM. */
export function lightSourceDirection(degrees: number): WorldPoint {
    const angle = degrees * Math.PI / 180;
    return {x: Math.sin(angle), y: -Math.cos(angle)};
}

export function pointLightFalloff(distanceMeters: number, radiusMeters: number): number {
    if (!(radiusMeters > 0)) return 0;
    const value = Math.max(0, Math.min(1, distanceMeters / radiusMeters));
    return 1 - value * value * (3 - 2 * value);
}

export function insideLightingBounds(point: WorldPoint, bounds: LightingBounds): boolean {
    return point.x >= bounds.minX && point.x <= bounds.maxX && point.y >= bounds.minY && point.y <= bounds.maxY;
}

export function directionalLightReaches(point: WorldPoint, scene: LightingEnvironment): boolean {
    if (scene.directionalLight.mode === "DARKNESS" || !insideLightingBounds(point, scene.bounds)) return false;
    const direction = lightSourceDirection(scene.directionalLight.directionDegrees);
    const {bounds} = scene;
    const distanceX = Math.abs(direction.x) < 1e-10 ? Infinity
        : ((direction.x > 0 ? bounds.maxX : bounds.minX) - point.x) / direction.x;
    const distanceY = Math.abs(direction.y) < 1e-10 ? Infinity
        : ((direction.y > 0 ? bounds.maxY : bounds.minY) - point.y) / direction.y;
    const distance = Math.min(distanceX, distanceY) + 0.01;
    return hasLineOfSight(point, {x: point.x + direction.x * distance, y: point.y + direction.y * distance}, scene.blockers);
}

/** Observer-independent physical light. Max-compositing matches the GPU and avoids overexposure. */
export function illuminationAt(point: WorldPoint, scene: LightingScene, timeSeconds = 0, animated = true): {
    brightness: number; color: [number, number, number];
} {
    const result = {brightness: 0, color: [0, 0, 0] as [number, number, number]};
    if (!insideLightingBounds(point, scene.bounds)) return result;
    const merge = (intensity: number, strength: number, color: string, factor: number) => {
        result.brightness = Math.max(result.brightness, intensity * factor);
        for (let channel = 0; channel < 3; channel++) result.color[channel] = Math.max(result.color[channel],
            parseInt(color.slice(1 + channel * 2, 3 + channel * 2), 16) / 255 * strength * factor);
    };
    if (directionalLightReaches(point, scene)) {
        const light = scene.directionalLight;
        merge(light.intensity, light.colorStrength, light.color, 1);
    }
    for (const light of scene.lights) {
        const falloff = pointLightFalloff(Math.hypot(point.x - light.origin.x, point.y - light.origin.y)
            / PIXELS_PER_METER, light.radiusMeters);
        if (falloff <= 0 || !hasLineOfSight(light.origin, point, scene.blockers)) continue;
        merge(light.intensity, light.colorStrength, light.color,
            falloff * (animated ? lightAnimationFactor(light.animation, light.id ?? "", timeSeconds) : 1));
    }
    return result;
}
